import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Public Terms of Service page.
 * Route: /terms
 */
export default function Terms() {
  const effectiveDate = '30 April 2026';

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm opacity-80 hover:opacity-100 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Taptile
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold">Terms of Service</h1>
          <p className="text-sm opacity-80 mt-2">Effective date: {effectiveDate}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8 leading-relaxed">
        <Section title="1. Agreement">
          <p>
            These Terms govern your use of Taptile (the &ldquo;Service&rdquo;), provided by Dynatex SaaS Co., Th
            (&ldquo;we&rdquo;, &ldquo;us&rdquo;), a company registered in Thailand. By creating an account, installing our iOS app, or otherwise using
            the Service, you agree to these Terms. If you do not agree, do not use the Service.
          </p>
        </Section>

        <Section title="2. Eligibility">
          <p>
            You must be at least 13 years old to create an account. If you use Taptile on behalf of an organisation,
            you confirm you are authorised to bind that organisation to these Terms.
          </p>
        </Section>

        <Section title="3. Your account">
          <ul className="list-disc pl-6 space-y-1">
            <li>You are responsible for keeping your login credentials confidential.</li>
            <li>You are responsible for activity that occurs under your account.</li>
            <li>You must notify us promptly of any unauthorised use.</li>
            <li>Admins of an organisation may create, modify, or delete accounts within their tenant.</li>
          </ul>
        </Section>

        <Section title="4. Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Use the Service for any unlawful purpose</li>
            <li>Send notifications or content that is harassing, defamatory, hateful, sexually explicit, or otherwise inappropriate</li>
            <li>Attempt to reverse-engineer, decompile, or circumvent security measures</li>
            <li>Use automated tools (bots, scrapers) except via documented APIs with a valid API key</li>
            <li>Resell or sublicense the Service without written permission</li>
            <li>Upload content you do not own or have permission to share</li>
          </ul>
          <p>
            We may suspend or terminate accounts that violate these rules.
          </p>
        </Section>

        <Section title="5. Subscriptions and billing">
          <ul className="list-disc pl-6 space-y-1">
            <li>Paid tenant plans are billed monthly or annually via Stripe.</li>
            <li>Fees are non-refundable except where required by law.</li>
            <li>We may change pricing with 30 days notice; existing terms apply until the end of your current billing period.</li>
            <li>You may cancel your subscription at any time; service continues until the end of the paid period.</li>
          </ul>
        </Section>

        <Section title="6. Content ownership">
          <ul className="list-disc pl-6 space-y-1">
            <li>You retain ownership of content you upload (tiles, articles, images, contacts).</li>
            <li>You grant Taptile a limited, worldwide licence to store, process, and display that content solely to provide the Service to you.</li>
            <li>We do not use your content to train machine-learning models.</li>
            <li>We do not sell your data.</li>
          </ul>
        </Section>

        <Section title="7. Third-party integrations">
          <p>
            Taptile integrates with third-party services such as NexusCMS, Wonde, Google, Microsoft, Stripe, SendGrid,
            and Expo. Your use of those services is governed by their own terms; we are not responsible for their conduct
            or content.
          </p>
        </Section>

        <Section title="8. Availability and changes">
          <p>
            We aim for high uptime but do not guarantee uninterrupted service. We may change, suspend, or discontinue
            features with reasonable notice. Maintenance windows are scheduled outside UK business hours where possible.
          </p>
        </Section>

        <Section title="9. Termination">
          <ul className="list-disc pl-6 space-y-1">
            <li>You may close your account at any time by emailing us.</li>
            <li>We may suspend or terminate your access immediately if you breach these Terms.</li>
            <li>On termination, your data is deleted within 30 days, except where retained for legal or audit purposes (see Privacy Policy).</li>
          </ul>
        </Section>

        <Section title="10. Disclaimers">
          <p>
            The Service is provided &ldquo;as is&rdquo;. To the maximum extent permitted by law, we disclaim all warranties,
            express or implied, including merchantability, fitness for a particular purpose, and non-infringement.
          </p>
        </Section>

        <Section title="11. Limitation of liability">
          <p>
            To the maximum extent permitted by law, our total liability arising from or related to the Service is
            limited to the amount you paid Taptile in the 12 months preceding the claim. We are not liable for indirect,
            incidental, or consequential damages.
          </p>
        </Section>

        <Section title="12. Governing law">
          <p>
            These Terms are governed by the laws of Thailand. Disputes will be resolved in the courts of
            Thailand, unless mandatory local consumer-protection law in your country of residence requires otherwise.
          </p>
        </Section>

        <Section title="13. Changes to these Terms">
          <p>
            We may update these Terms periodically. Material changes will be communicated by email or in-app notice
            at least 14 days before they take effect.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>
            <a className="text-blue-600 underline" href="mailto:taptile@dynatexsaas.com">taptile@dynatexsaas.com</a>
          </p>
          <p>
            Dynatex SaaS Co., Th, registered in Thailand
          </p>
        </Section>

        <div className="pt-8 border-t text-sm text-slate-500">
          <Link to="/" className="text-blue-600 underline">&larr; Return to taptile.app</Link>
          {' \u00B7 '}
          <Link to="/privacy" className="text-blue-600 underline">Privacy Policy</Link>
          {' \u00B7 '}
          <Link to="/support" className="text-blue-600 underline">Support</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-bold text-slate-900 mt-6 mb-2 border-b-2 border-orange-500 pb-1">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
